import { Navigate, Route, Routes } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuth } from './hooks/useAuth';
import { usePermission } from './hooks/usePermission';
import { useAuthStore } from './store/auth.store';
import { AppLayout } from './components/layout/AppLayout';
import { LoadingSpinner } from './components/common/LoadingSpinner';

// Auth
import { LoginPage } from './pages/auth/LoginPage';

// Admin
import { SuperAdminDashboard } from './pages/admin/SuperAdminDashboard';
import { TenantsPage } from './pages/admin/TenantsPage';
import { PlansPage } from './pages/admin/PlansPage';

// Tenant
import { TenantDashboard } from './pages/tenant/TenantDashboard';
import { EmployeesPage } from './pages/tenant/EmployeesPage';
import { CustomersPage } from './pages/tenant/CustomersPage';
import { GeofencesPage } from './pages/tenant/GeofencesPage';
import { VisitAssignmentsPage } from './pages/tenant/VisitAssignmentsPage';
import { ReportsPage } from './pages/tenant/ReportsPage';

// Manager
import { ManagerDashboard } from './pages/manager/ManagerDashboard';
import { LiveMapPage } from './pages/manager/LiveMapPage';
import { VisitManagementPage } from './pages/manager/VisitManagementPage';
import { TrackingAlertsPage } from './pages/manager/TrackingAlertsPage';

import { Role } from './types';

// ─── Protected Route ──────────────────────────────────────────────────────────

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: Role[];
}

function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    // Redirect to the correct home for their role
    const homeMap: Record<Role, string> = {
      [Role.PLATFORM_SUPER_ADMIN]: '/admin/dashboard',
      [Role.TENANT_ADMIN]: '/tenant/dashboard',
      [Role.MANAGER]: '/manager/dashboard',
      [Role.EMPLOYEE]: '/login',
    };
    return <Navigate to={homeMap[user.role] ?? '/login'} replace />;
  }

  return <>{children}</>;
}

// ─── Home Redirect ────────────────────────────────────────────────────────────

function HomeRedirect() {
  const { isAuthenticated, user } = useAuth();
  const { getHomeRoute } = usePermission();
  const clearAuth = useAuthStore((state) => state.actions.clearAuth);

  useEffect(() => {
    // Employee (and any role without a web home) must not bounce / ↔ /login.
    if (isAuthenticated && user?.role === Role.EMPLOYEE) {
      clearAuth();
    }
  }, [isAuthenticated, user, clearAuth]);

  if (!isAuthenticated || user?.role === Role.EMPLOYEE) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={getHomeRoute()} replace />;
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const { isAuthenticated, user } = useAuth();
  const hasHydrated = useAuthStore((state) => state.hasHydrated);
  const initializeFromStorage = useAuthStore((state) => state.actions.initializeFromStorage);

  // Wait for zustand persist rehydration, then reconcile with token keys.
  useEffect(() => {
    const finish = () => initializeFromStorage();
    if (useAuthStore.persist.hasHydrated()) {
      finish();
      return;
    }
    return useAuthStore.persist.onFinishHydration(finish);
  }, [initializeFromStorage]);

  if (!hasHydrated) {
    return <LoadingSpinner fullPage />;
  }

  const canUseWebConsole =
    isAuthenticated && !!user && user.role !== Role.EMPLOYEE;

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={canUseWebConsole ? <Navigate to="/" replace /> : <LoginPage />}
      />

      {/* Root - redirect based on role */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Protected Routes with Layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* ── PLATFORM SUPER ADMIN ── */}
        <Route
          path="admin/dashboard"
          element={
            <ProtectedRoute allowedRoles={[Role.PLATFORM_SUPER_ADMIN]}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/tenants"
          element={
            <ProtectedRoute allowedRoles={[Role.PLATFORM_SUPER_ADMIN]}>
              <TenantsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/plans"
          element={
            <ProtectedRoute allowedRoles={[Role.PLATFORM_SUPER_ADMIN]}>
              <PlansPage />
            </ProtectedRoute>
          }
        />

        {/* ── TENANT ADMIN ── */}
        <Route
          path="tenant/dashboard"
          element={
            <ProtectedRoute allowedRoles={[Role.TENANT_ADMIN]}>
              <TenantDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="tenant/employees"
          element={
            <ProtectedRoute allowedRoles={[Role.TENANT_ADMIN]}>
              <EmployeesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tenant/customers"
          element={
            <ProtectedRoute allowedRoles={[Role.TENANT_ADMIN]}>
              <CustomersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tenant/geofences"
          element={
            <ProtectedRoute allowedRoles={[Role.TENANT_ADMIN]}>
              <GeofencesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tenant/visits"
          element={
            <ProtectedRoute allowedRoles={[Role.TENANT_ADMIN]}>
              <VisitAssignmentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tenant/reports"
          element={
            <ProtectedRoute allowedRoles={[Role.TENANT_ADMIN]}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />

        {/* ── MANAGER ── */}
        <Route
          path="manager/dashboard"
          element={
            <ProtectedRoute allowedRoles={[Role.MANAGER]}>
              <ManagerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="manager/map"
          element={
            <ProtectedRoute allowedRoles={[Role.MANAGER]}>
              <LiveMapPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="manager/visits"
          element={
            <ProtectedRoute allowedRoles={[Role.MANAGER]}>
              <VisitManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="manager/alerts"
          element={
            <ProtectedRoute allowedRoles={[Role.MANAGER]}>
              <TrackingAlertsPage />
            </ProtectedRoute>
          }
        />

        {/* Manager team page (same as employees but filtered) */}
        <Route
          path="manager/team"
          element={
            <ProtectedRoute allowedRoles={[Role.MANAGER]}>
              <EmployeesPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* 404 - catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
